// src/store/slices/dnlToolsSlice.ts
/**
 * DNL (Double Number Line) Tools Redux Slice
 * Phase 1: Custom/Sparse Points System
 * 
 * Manages DNL-specific state including:
 * - Sparse tick mode
 * - Custom points for primary and secondary lines
 * - Line labels
 * - Unit suffixes
 * 
 * Based on DNL Enhancement Specification v1.0
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  DNLToolsState,
  DNLCustomTick,
  SparseTickMode,
  LineLabel,
  UnitSuffix,
} from '../../types/dnl';
import {
  defaultDNLToolsState,
  defaultSparseTickMode,
  defaultLineLabel,
  defaultUnitSuffix,
} from '../../types/dnl';
import type { Fraction } from '../../types/redux';

// =============================================================================
// STORAGE KEY
// =============================================================================

const DNL_TOOLS_STORAGE_KEY = 'nl:dnlTools';

// =============================================================================
// STORAGE HELPERS
// =============================================================================

/**
 * Load DNL tools state from localStorage
 */
function loadFromStorage(): DNLToolsState {
  try {
    const stored = localStorage.getItem(DNL_TOOLS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new properties
      return {
        sparseMode: { ...defaultSparseTickMode, ...parsed.sparseMode },
        primaryLabel: { ...defaultLineLabel, ...parsed.primaryLabel },
        secondaryLabel: { ...defaultLineLabel, ...parsed.secondaryLabel },
        primaryUnit: { ...defaultUnitSuffix, ...parsed.primaryUnit },
        secondaryUnit: { ...defaultUnitSuffix, ...parsed.secondaryUnit },
      };
    }
  } catch (error) {
    console.warn('Failed to load DNL tools from storage:', error);
  }
  return { ...defaultDNLToolsState };
}

/**
 * Save DNL tools state to localStorage
 */
function saveToStorage(state: DNLToolsState): void {
  try {
    localStorage.setItem(DNL_TOOLS_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save DNL tools to storage:', error);
  }
}

// =============================================================================
// INITIAL STATE
// =============================================================================

// Re-export defaults for use in this file
const defaultSparseTickModeState: SparseTickMode = {
  enabled: false,
  hideRegularTicks: true,
  customTicks: [],
  showZero: true,
  nextId: 1,
};

const defaultLineLabelState: LineLabel = {
  text: '',
  subtext: undefined,
  position: 'left',
  visible: false,
};

const defaultUnitSuffixState: UnitSuffix = {
  text: '',
  position: 'after',
  enabled: false,
};

const defaultDNLToolsStateLocal: DNLToolsState = {
  sparseMode: defaultSparseTickModeState,
  primaryLabel: { ...defaultLineLabelState },
  secondaryLabel: { ...defaultLineLabelState },
  primaryUnit: { ...defaultUnitSuffixState },
  secondaryUnit: { ...defaultUnitSuffixState },
};

const initialState: DNLToolsState = loadFromStorage();

// =============================================================================
// SLICE DEFINITION
// =============================================================================

export const dnlToolsSlice = createSlice({
  name: 'dnlTools',
  initialState,
  reducers: {
    // =========================================================================
    // SPARSE MODE ACTIONS
    // =========================================================================

    /**
     * Toggle sparse tick mode on/off
     */
    toggleSparseMode: (state) => {
      state.sparseMode.enabled = !state.sparseMode.enabled;
      saveToStorage(state);
    },

    /**
     * Set sparse mode enabled state
     */
    setSparseModeEnabled: (state, action: PayloadAction<boolean>) => {
      state.sparseMode.enabled = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle hiding of regular ticks in sparse mode
     */
    toggleHideRegularTicks: (state) => {
      state.sparseMode.hideRegularTicks = !state.sparseMode.hideRegularTicks;
      saveToStorage(state);
    },

    /**
     * Set hide regular ticks state
     */
    setHideRegularTicks: (state, action: PayloadAction<boolean>) => {
      state.sparseMode.hideRegularTicks = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle showing zero in sparse mode
     */
    toggleShowZero: (state) => {
      state.sparseMode.showZero = !state.sparseMode.showZero;
      saveToStorage(state);
    },

    // =========================================================================
    // CUSTOM TICK ACTIONS
    // =========================================================================

    /**
     * Add a new custom tick
     */
    addCustomTick: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary' | 'both';
      label?: string;
      style?: 'normal' | 'highlighted' | 'question';
      colour?: string;
    }>) => {
      const { value, line, label, style = 'normal', colour } = action.payload;

      // Check for duplicate on the same line
      const existingTick = state.sparseMode.customTicks.find(
        tick => Math.abs(tick.value - value) < 1e-10 && 
                (tick.line === line || tick.line === 'both' || line === 'both')
      );

      if (!existingTick) {
        // Only show label if there's a custom label or it's a question mark
        const shouldShowLabel = !!(label || style === 'question');
        const newTick: DNLCustomTick = {
          id: `dnl-tick-${state.sparseMode.nextId}`,
          value,
          label,
          line,
          showTick: true,
          showLabel: shouldShowLabel,
          style,
          colour,
          createdAt: Date.now(),
        };

        state.sparseMode.customTicks.push(newTick);
        state.sparseMode.nextId += 1;
        saveToStorage(state);
      }
    },

    /**
     * Add a custom tick with a corresponding point on the other line
     * Note: value is expected to be in domain (primary) coordinates
     */
    addCustomTickWithCorresponding: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary';
      label?: string;
      style?: 'normal' | 'highlighted' | 'question';
      colour?: string;
      scaleFactor: Fraction;
    }>) => {
      const { value, line, label, style = 'normal', colour, scaleFactor } = action.payload;
      
      // Value is already in domain coordinates (primary line coordinate system)
      // Both primary and secondary ticks at the same domain position are "corresponding"
      // So the corresponding value is the same domain value
      const correspondingValue = value;

      // For corresponding points, don't set a custom label - let the renderer calculate
      // the numeric value from tick.value. Only set label if user provided a custom label
      // or if it's a question mark style.
      const tickLabel = label || (style === 'question' ? '?' : undefined);

      // Add tick on the target line
      const targetTickId = `dnl-tick-${state.sparseMode.nextId}`;
      const targetTick: DNLCustomTick = {
        id: targetTickId,
        value,
        label: tickLabel,
        line,
        showTick: true,
        showLabel: true, // Always show label for corresponding points (numeric value will be calculated)
        style,
        colour,
        createdAt: Date.now(),
      };

      state.sparseMode.customTicks.push(targetTick);
      state.sparseMode.nextId += 1;

      // Add corresponding tick on other line (same domain position)
      const otherLine = line === 'primary' ? 'secondary' : 'primary';
      const correspondingTickId = `dnl-tick-${state.sparseMode.nextId}`;
      const correspondingTick: DNLCustomTick = {
        id: correspondingTickId,
        value: correspondingValue,
        label: tickLabel, // Same label (or undefined to show numeric value)
        line: otherLine,
        showTick: true,
        showLabel: true, // Always show label for corresponding points (numeric value will be calculated)
        style,
        colour,
        createdAt: Date.now(),
        linkedTickId: targetTickId,
      };

      // Link the target tick to the corresponding tick
      targetTick.linkedTickId = correspondingTickId;

      state.sparseMode.customTicks.push(correspondingTick);
      state.sparseMode.nextId += 1;
      saveToStorage(state);
    },

    /**
     * Remove a custom tick by ID
     * @param removeLinked - If true, also removes the linked tick. Default: true for backward compatibility
     */
    removeCustomTick: (state, action: PayloadAction<{ id: string; removeLinked?: boolean } | string>) => {
      // Support both old format (string) and new format (object)
      const payload = typeof action.payload === 'string' 
        ? { id: action.payload, removeLinked: true }
        : action.payload;
      
      const { id, removeLinked = true } = payload;
      const tickToRemove = state.sparseMode.customTicks.find(t => t.id === id);
      
      if (!tickToRemove) return;
      
      // Find the linked tick BEFORE filtering (if it exists)
      const linkedTickId = tickToRemove.linkedTickId;
      const linkedTick = linkedTickId 
        ? state.sparseMode.customTicks.find(t => t.id === linkedTickId)
        : null;
      
      // Also remove linked tick if requested
      if (removeLinked && linkedTickId) {
        if (linkedTick) {
          // Linked tick exists - clean up its link before removing
          linkedTick.linkedTickId = undefined;
          // Remove both the tick and its linked tick
          state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
            t => t.id !== id && t.id !== linkedTickId
          );
        } else {
          // Linked tick doesn't exist (was already deleted) - just remove this tick
          // and clean up any references to it
          state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
            t => t.id !== id
          );
          // Clean up any ticks that reference this deleted tick
          state.sparseMode.customTicks.forEach(t => {
            if (t.linkedTickId === id) {
              t.linkedTickId = undefined;
            }
          });
        }
      } else {
        // Remove only this tick
        state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
          t => t.id !== id
        );
        // Remove the link from the linked tick if it exists and still exists in the array
        if (linkedTickId && linkedTick) {
          linkedTick.linkedTickId = undefined;
        }
        // Also check if any other tick has this tick as its linked tick and remove that link
        // This handles the case where a point was deleted but its linked point still references it
        state.sparseMode.customTicks.forEach(t => {
          if (t.linkedTickId === id) {
            t.linkedTickId = undefined;
          }
        });
      }
      
      saveToStorage(state);
    },

    /**
     * Remove a custom tick by value and line
     */
    removeCustomTickByValue: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary' | 'both';
    }>) => {
      const { value, line } = action.payload;
      state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
        tick => !(Math.abs(tick.value - value) < 1e-10 && 
                 (tick.line === line || tick.line === 'both' || line === 'both'))
      );
      saveToStorage(state);
    },

    /**
     * Update a custom tick
     */
    updateCustomTick: (state, action: PayloadAction<{
      id: string;
      updates: Partial<Omit<DNLCustomTick, 'id' | 'createdAt'>>;
    }>) => {
      const { id, updates } = action.payload;
      const tick = state.sparseMode.customTicks.find(t => t.id === id);
      if (tick) {
        Object.assign(tick, updates);
        saveToStorage(state);
      }
    },

    /**
     * Toggle tick visibility
     */
    toggleCustomTickVisible: (state, action: PayloadAction<string>) => {
      const tick = state.sparseMode.customTicks.find(t => t.id === action.payload);
      if (tick) {
        tick.showTick = !tick.showTick;
        saveToStorage(state);
      }
    },

    /**
     * Toggle label visibility for a custom tick
     */
    toggleCustomTickLabelVisible: (state, action: PayloadAction<string>) => {
      const tick = state.sparseMode.customTicks.find(t => t.id === action.payload);
      if (tick) {
        tick.showLabel = !tick.showLabel;
        saveToStorage(state);
      }
    },

    /**
     * Set custom tick style (for "?" unknown values)
     */
    setCustomTickStyle: (state, action: PayloadAction<{
      id: string;
      style: 'normal' | 'highlighted' | 'question';
    }>) => {
      const { id, style } = action.payload;
      const tick = state.sparseMode.customTicks.find(t => t.id === id);
      if (tick) {
        tick.style = style;
        // If setting to question style, also set the label to "?"
        if (style === 'question' && !tick.label) {
          tick.label = '?';
        }
        saveToStorage(state);
      }
    },

    /**
     * Clear all custom ticks
     */
    clearAllCustomTicks: (state) => {
      state.sparseMode.customTicks = [];
      state.sparseMode.nextId = 1;
      saveToStorage(state);
    },

    /**
     * Clear custom ticks for a specific line only
     */
    clearCustomTicksForLine: (state, action: PayloadAction<'primary' | 'secondary'>) => {
      state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
        tick => tick.line !== action.payload && tick.line !== 'both'
      );
      saveToStorage(state);
    },

    // =========================================================================
    // LINE LABEL ACTIONS
    // =========================================================================

    /**
     * Set primary line label
     */
    setPrimaryLabel: (state, action: PayloadAction<Partial<LineLabel>>) => {
      state.primaryLabel = { ...state.primaryLabel, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Set secondary line label
     */
    setSecondaryLabel: (state, action: PayloadAction<Partial<LineLabel>>) => {
      state.secondaryLabel = { ...state.secondaryLabel, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Toggle primary label visibility
     */
    togglePrimaryLabelVisible: (state) => {
      state.primaryLabel.visible = !state.primaryLabel.visible;
      saveToStorage(state);
    },

    /**
     * Toggle secondary label visibility
     */
    toggleSecondaryLabelVisible: (state) => {
      state.secondaryLabel.visible = !state.secondaryLabel.visible;
      saveToStorage(state);
    },

    // =========================================================================
    // UNIT SUFFIX ACTIONS
    // =========================================================================

    /**
     * Set primary line unit suffix
     */
    setPrimaryUnit: (state, action: PayloadAction<Partial<UnitSuffix>>) => {
      state.primaryUnit = { ...state.primaryUnit, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Set secondary line unit suffix
     */
    setSecondaryUnit: (state, action: PayloadAction<Partial<UnitSuffix>>) => {
      state.secondaryUnit = { ...state.secondaryUnit, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Toggle primary unit enabled
     */
    togglePrimaryUnitEnabled: (state) => {
      state.primaryUnit.enabled = !state.primaryUnit.enabled;
      saveToStorage(state);
    },

    /**
     * Toggle secondary unit enabled
     */
    toggleSecondaryUnitEnabled: (state) => {
      state.secondaryUnit.enabled = !state.secondaryUnit.enabled;
      saveToStorage(state);
    },

    // =========================================================================
    // BULK ACTIONS
    // =========================================================================

    /**
     * Reset all DNL tools to defaults
     */
    resetDNLTools: (state) => {
      Object.assign(state, defaultDNLToolsStateLocal);
      saveToStorage(state);
    },

    /**
     * Load DNL tools state (e.g., from shared scene)
     */
    loadDNLToolsState: (state, action: PayloadAction<DNLToolsState>) => {
      Object.assign(state, action.payload);
      saveToStorage(state);
    },
  },
});

// =============================================================================
// EXPORTS
// =============================================================================

export const {
  // Sparse mode
  toggleSparseMode,
  setSparseModeEnabled,
  toggleHideRegularTicks,
  setHideRegularTicks,
  toggleShowZero,
  // Custom ticks
  addCustomTick,
  addCustomTickWithCorresponding,
  removeCustomTick,
  removeCustomTickByValue,
  updateCustomTick,
  toggleCustomTickVisible,
  toggleCustomTickLabelVisible,
  setCustomTickStyle,
  clearAllCustomTicks,
  clearCustomTicksForLine,
  // Line labels
  setPrimaryLabel,
  setSecondaryLabel,
  togglePrimaryLabelVisible,
  toggleSecondaryLabelVisible,
  // Unit suffixes
  setPrimaryUnit,
  setSecondaryUnit,
  togglePrimaryUnitEnabled,
  toggleSecondaryUnitEnabled,
  // Bulk actions
  resetDNLTools,
  loadDNLToolsState,
} = dnlToolsSlice.actions;

export default dnlToolsSlice.reducer;

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Select all custom ticks
 */
export const selectAllCustomTicks = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode.customTicks;

/**
 * Select custom ticks for a specific line
 */
export const selectCustomTicksForLine = (
  state: { dnlTools: DNLToolsState },
  line: 'primary' | 'secondary'
) =>
  state.dnlTools.sparseMode.customTicks.filter(
    tick => tick.line === line || tick.line === 'both'
  );

/**
 * Select custom ticks within a domain
 */
export const selectCustomTicksInDomain = (
  state: { dnlTools: DNLToolsState },
  domain: { start: number; end: number },
  line?: 'primary' | 'secondary'
) =>
  state.dnlTools.sparseMode.customTicks.filter(tick => {
    const inDomain = tick.value >= domain.start && tick.value <= domain.end;
    const matchesLine = !line || tick.line === line || tick.line === 'both';
    return inDomain && matchesLine;
  });

/**
 * Select sparse mode state
 */
export const selectSparseMode = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode;

/**
 * Check if sparse mode is enabled
 */
export const selectIsSparseModeEnabled = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode.enabled;

/**
 * Select line labels
 */
export const selectLineLabels = (state: { dnlTools: DNLToolsState }) => ({
  primary: state.dnlTools.primaryLabel,
  secondary: state.dnlTools.secondaryLabel,
});

/**
 * Select unit suffixes
 */
export const selectUnitSuffixes = (state: { dnlTools: DNLToolsState }) => ({
  primary: state.dnlTools.primaryUnit,
  secondary: state.dnlTools.secondaryUnit,
});
